import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ChevronLeft, ChevronRight, DoorOpen, Percent, TrendingUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getSessionToken } from "@/utils/auth";

const BACKEND_URL =
  window.APP_CONFIG?.BACKEND_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  window.location.origin;
const API = `${BACKEND_URL}/api`;

const fmtMoney = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const MESH_LABELS = { standard: "Standard", solar: "Solar", petscreen: "Pet Screen", no_see_um: "No-See-Um" };
const COLOR_LABELS = { white: "White", almond: "Almond", black: "Black" };
const WIN_LABELS = { rescreen: "Rescreen", new: "New", both: "Both", no: "No thanks" };

export default function ScreenDoorFunnelAnalytics() {
  const navigate = useNavigate();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const token = getSessionToken();
      const { data } = await axios.get(`${API}/reports/screen-door-funnel`, {
        params: { month: `${year}-${String(month).padStart(2, "0")}` },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch {
      toast.error("Failed to load screen door funnel report");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year, month]);

  const prev = () => { if (month === 1) { setYear(year - 1); setMonth(12); } else setMonth(month - 1); };
  const next = () => { if (month === 12) { setYear(year + 1); setMonth(1); } else setMonth(month + 1); };
  const isCurrent = year === now.getFullYear() && month === now.getMonth() + 1;

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button onClick={() => navigate("/settings")} data-testid="back" className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> <span className="text-sm font-medium">Back to Settings</span>
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-lg bg-[#207024] text-white flex items-center justify-center">
            <DoorOpen className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Screen Door Funnel</h1>
            <p className="text-sm text-zinc-500">V4 configurator performance — <span className="font-mono text-xs">/screen-door-quote</span></p>
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm p-3 flex items-center justify-between mb-6" data-testid="month-switcher">
          <button onClick={prev} data-testid="prev-month" className="h-10 w-10 rounded-lg border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="text-lg font-semibold text-zinc-900">{MONTHS[month - 1]} {year}</div>
            {isCurrent && <div className="text-[11px] text-emerald-600 font-medium">Current month</div>}
          </div>
          <button onClick={next} data-testid="next-month" disabled={isCurrent}
            className="h-10 w-10 rounded-lg border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center disabled:opacity-40">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-zinc-400 animate-spin" />
          </div>
        ) : !data ? null : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Stat label="Visits" value={data.visits} tint="zinc" />
              <Stat label="Leads" value={data.submitted} tint="blue" />
              <Stat label="Conversion" value={`${data.conversion_rate}%`} icon={<Percent className="w-3.5 h-3.5" />} tint="emerald" />
              <Stat label="Avg Quote" value={fmtMoney(data.quote_value?.avg)} tint="purple" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <Stat label="Pipeline" value={fmtMoney(data.quote_value?.total_pipeline)} tint="purple" />
              <Stat label="Booked" value={data.closed?.booked_count} tint="emerald" />
              <Stat label="Close Rate" value={`${data.closed?.close_rate}%`} icon={<TrendingUp className="w-3.5 h-3.5" />} tint="emerald" />
              <Stat label="Closed Revenue" value={fmtMoney(data.closed?.revenue)} tint="emerald" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <BreakdownCard title="New Door vs Rescreen" items={data.service_split} total={data.submitted}
                labelMap={{ new: "New Door", rescreen: "Rescreen" }} testId="service-split" />
              <BreakdownCard title="Heavy Duty Take Rate (new doors)"
                items={{ Upgraded: data.heavy_duty?.yes || 0, Standard: data.heavy_duty?.no || 0 }}
                total={(data.heavy_duty?.yes || 0) + (data.heavy_duty?.no || 0)}
                trailingBadge={`${data.heavy_duty?.take_rate || 0}%`}
                testId="hd-split" />
              <BreakdownCard title="Frame Color" items={data.color_picks} total={data.submitted}
                labelMap={COLOR_LABELS} testId="color-picks" />
              <BreakdownCard title="Mesh Pick" items={data.mesh_picks} total={data.submitted}
                labelMap={MESH_LABELS} testId="mesh-picks" />
              <BreakdownCard title="Window Cross-Sell" items={data.window_cross_sell?.choices}
                total={data.submitted} labelMap={WIN_LABELS}
                trailingBadge={`${data.window_cross_sell?.attach_rate || 0}% attach`}
                testId="window-split" />
            </div>

            {data.submitted === 0 && (
              <p className="text-center text-sm text-zinc-500 mt-8">
                No V4 leads yet this month. Point ad traffic at <span className="font-mono">/screen-door-quote</span> and check back.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, icon, tint }) {
  const tints = {
    zinc: "bg-zinc-50 text-zinc-700 border-zinc-100",
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
  };
  return (
    <div className={`rounded-xl border p-3 ${tints[tint] || tints.zinc}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-80">
        {icon}{label}
      </div>
      <div className="text-xl font-bold mt-1">{value ?? 0}</div>
    </div>
  );
}

function BreakdownCard({ title, items = {}, total, labelMap = {}, trailingBadge, testId }) {
  const entries = Object.entries(items || {}).sort((a, b) => b[1] - a[1]);
  const hasData = entries.some(([, v]) => v > 0);
  return (
    <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid={testId}>
      <div className="p-4 border-b border-zinc-100 flex items-center justify-between">
        <h2 className="font-semibold text-zinc-900 text-sm">{title}</h2>
        {trailingBadge && (
          <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
            {trailingBadge}
          </span>
        )}
      </div>
      {!hasData ? (
        <div className="p-6 text-center text-sm text-zinc-400">No picks yet</div>
      ) : (
        <div className="p-4 space-y-2">
          {entries.map(([k, v]) => {
            const pct = total > 0 ? Math.round((v / total) * 100) : 0;
            return (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-zinc-700">{labelMap[k] || k}</span>
                  <span className="text-zinc-500">{v} <span className="text-zinc-400">({pct}%)</span></span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600" style={{ width: `${Math.max(4, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
